import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';

interface SwitchProps extends React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> {
  /** Extra classes for the sliding thumb — the base component doesn't
   * otherwise expose a way to resize/restyle it per call site. */
  thumbClassName?: string;
}

/**
 * Switch after the reference design: a 34 × 20 track that is the primary
 * when on and the third paper with a hairline when off, a 16 px knob.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, thumbClassName, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      'peer inline-flex h-5 w-[34px] shrink-0 cursor-pointer items-center rounded-pill px-[2px] transition-[background-color,box-shadow] duration-[200ms]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
      'disabled:cursor-not-allowed disabled:opacity-40',
      'data-[state=checked]:bg-primary data-[state=unchecked]:bg-paper-3 data-[state=unchecked]:shadow-[inset_0_0_0_1px_var(--line)]',
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block h-4 w-4 rounded-pill bg-paper shadow-[0_0_0_1px_rgba(13,13,13,0.08)] transition-transform duration-[200ms]',
        'data-[state=checked]:translate-x-[14px] data-[state=unchecked]:translate-x-0',
        thumbClassName
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
